import React, { useState, useEffect } from 'react';
import axios from '../config/axiosConfig';
import './RequestModal.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext';
import {
  FaBuilding, FaUser, FaCheck, FaArrowRight, FaArrowLeft, FaTimes,
  FaSignInAlt, FaGraduationCap, FaClock,
  FaCertificate, FaCheckCircle, FaTimesCircle,
  FaExclamationTriangle, FaInfoCircle
} from 'react-icons/fa';

const RequestModal = ({ onClose, departmentId, selectedWoman, onSubmit }) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();

  // ========== TOAST STATE ==========
  const [toasts, setToasts] = useState([]);
  let toastIdCounter = 0;

  const showToast = (type, title, message, duration = 4000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, type, title, message, duration };

    setToasts(prev => [...prev, newToast]);

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // ========== MODAL STATE ==========
  const [step, setStep] = useState(1);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginData, setLoginData] = useState({ email: '' });
  const [success, setSuccess] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [registrationError, setRegistrationError] = useState('');

  // Contract period states
  const [competenceSkills, setCompetenceSkills] = useState([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState([]);
  const [contractPeriods, setContractPeriods] = useState([]);
  const [selectedContractPeriod, setSelectedContractPeriod] = useState('');
  const [loadingContractPeriods, setLoadingContractPeriods] = useState(false);

  const [formData, setFormData] = useState({
    user_type: 'individual',
    company_name: '',
    username: '',
    email: '',
    full_name: '',
    phone: '',
    department_id: departmentId || '',
    number_of_employees: selectedWoman ? 1 : '',
    work_type: 'contract',
    contract_period_id: '',
    employer_name: '',
    employer_email: '',
    employer_phone: '',
    requested_woman_id: selectedWoman ? selectedWoman.id : null,
    requested_woman_name: selectedWoman ? selectedWoman.name : '',
    age_range: '',
    experience_level: '',
    skill_level: '',
    education_level: '',
    language_requirements: '',
    competence_skills: '',
    other_specifications: ''
  });

  // Clean axios instance for login/register requests (no auth headers)
  const cleanAxios = axios.create();

  // ========== Fetch contract periods ==========
  const fetchContractPeriods = async () => {
    try {
      setLoadingContractPeriods(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setLoadingContractPeriods(false);
        return;
      }

      const response = await axios.get('https://hsn.urugowoc.org.rw/backend/api/contract-periods', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setContractPeriods(response.data);

      if (response.data.length > 0 && !selectedContractPeriod) {
        setSelectedContractPeriod(response.data[0].id.toString());
        setFormData(prev => ({
          ...prev,
          contract_period_id: response.data[0].id.toString()
        }));
      }
      setLoadingContractPeriods(false);
    } catch (error) {
      console.error('Error fetching contract periods:', error);
      setLoadingContractPeriods(false);
    }
  };

  // ========== Fetch competence skills when department changes ==========
  useEffect(() => {
    const fetchCompetenceSkills = async () => {
      if (formData.department_id) {
        try {
          const token = localStorage.getItem('token');
          if (!token) return;

          const response = await axios.get(
            `https://hsn.urugowoc.org.rw/backend/api/competence-skills/${formData.department_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCompetenceSkills(response.data);
          setSelectedSkillIds([]);
        } catch (error) {
          console.error('Error fetching competence skills:', error);
        }
      }
    };

    fetchCompetenceSkills();
  }, [formData.department_id]);

  // ========== Initial setup ==========
useEffect(() => {
  fetchDepartments();

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const loggedIn = !!(token && user.id && user.user_type === 'employer');
  setIsLoggedIn(loggedIn);

  if (loggedIn) {
    // ✅ Logged-in employer: jump straight to final step (3), skip 1 & 2
    setFormData(prev => ({
      ...prev,
      employer_name: user.full_name || user.username || '',
      employer_email: user.email || '',
      employer_phone: user.phone || '',
      company_name: user.company_name || prev.company_name,
      user_type: user.company_name ? 'company' : 'individual'
    }));

    setStep(3);
    fetchContractPeriods();

    if (departmentId) {
      setFormData(prev => ({ ...prev, department_id: departmentId }));
    }

    if (selectedWoman) {
      setFormData(prev => ({
        ...prev,
        department_id: selectedWoman.department_id || departmentId,
        number_of_employees: 1,
        requested_woman_id: selectedWoman.id,
        requested_woman_name: selectedWoman.name
      }));
    }
  } else {
    // Not logged in: start at step 1
    if (departmentId) {
      setFormData(prev => ({ ...prev, department_id: departmentId }));
    }

    if (selectedWoman) {
      setFormData(prev => ({
        ...prev,
        department_id: selectedWoman.department_id || departmentId,
        number_of_employees: 1,
        requested_woman_id: selectedWoman.id,
        requested_woman_name: selectedWoman.name
      }));
    }
  }
}, [departmentId, selectedWoman]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchContractPeriods();
    }
  }, [isLoggedIn]);

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('https://hsn.urugowoc.org.rw/backend/api/departments', {
        params: { lang: language }
      });
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (registrationError) setRegistrationError('');
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    if (loginError) setLoginError('');
  };

  // ========== LOGIN HANDLER (EMAIL ONLY — EMPLOYERS) ==========
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');

    if (!loginData.email || !loginData.email.includes('@')) {
      setLoginError('Please enter a valid email address');
      showToast('error', t('alertError') || 'Error', 'Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await cleanAxios.post(
        'https://hsn.urugowoc.org.rw/backend/api/login-employer',
        { email: loginData.email }
      );

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      setIsLoggedIn(true);

      setFormData(prev => ({
        ...prev,
        employer_name: response.data.user.full_name || '',
        employer_email: response.data.user.email || '',
        employer_phone: response.data.user.phone || ''
      }));

      setStep(3);
      await fetchContractPeriods();

      // SUCCESS TOAST
      showToast(
        'success',
        t('alertSuccess') || 'Success',
        t('loginSuccess') || 'You have successfully logged in to your account.'
      );

    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = '';

      if (error.response) {
        errorMessage = error.response.data?.error || 'Login failed. No account found with this email.';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = 'Login failed';
      }

      setLoginError(errorMessage);
      showToast('error', t('alertError') || 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ========== REGISTRATION HANDLER (EMAIL ONLY, NO PASSWORD, NO VERIFICATION) ==========
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setRegistrationError('');

    if (!formData.email || !formData.email.includes('@')) {
      setRegistrationError('Please enter a valid email address');
      showToast('error', t('alertError') || 'Error', 'Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (!formData.full_name || formData.full_name.trim().length < 2) {
      setRegistrationError('Please enter your full name');
      showToast('error', t('alertError') || 'Error', 'Please enter your full name');
      setLoading(false);
      return;
    }

    try {
      const response = await cleanAxios.post(
        'https://hsn.urugowoc.org.rw/backend/api/register',
        {
          email: formData.email,
          full_name: formData.full_name,
          phone: formData.phone || null,
          user_type: 'employer',
          company_name: formData.company_name || null
        }
      );

      if (response.data.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        setIsLoggedIn(true);
        setFormData(prev => ({
          ...prev,
          employer_name: response.data.user.full_name || '',
          employer_email: response.data.user.email || '',
          employer_phone: response.data.user.phone || ''
        }));

        // SUCCESS TOAST
        showToast(
          'success',
          t('alertSuccess') || 'Success',
          t('registrationSuccess') || 'Your account has been created successfully!',
          5000
        );

        // Move to step 3 after a short delay (so user sees the toast)
        setTimeout(() => {
          setStep(3);
          fetchContractPeriods();
        }, 700);

      } else {
        throw new Error(response.data.error || 'Registration failed');
      }

    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = '';

      if (error.response) {
        errorMessage = error.response.data?.error || 'Registration failed';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'Registration failed';
      }

      setRegistrationError(errorMessage);
      showToast('error', t('alertError') || 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ========== SUBMIT REQUEST ==========
  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const selectedPeriod = contractPeriods.find(
        period => period.id === parseInt(selectedContractPeriod)
      );
      const duration_days = selectedPeriod ? selectedPeriod.months * 30 : 90;

      const requestData = {
        department_id: parseInt(formData.department_id),
        number_of_employees: parseInt(formData.number_of_employees),
        work_type: formData.work_type,
        duration_days: duration_days,
        employer_name: formData.employer_name,
        employer_email: formData.employer_email,
        employer_phone: formData.employer_phone,
        age_range: formData.age_range || null,
        experience_level: formData.experience_level || null,
        skill_level: formData.skill_level || null,
        education_level: formData.education_level || null,
        language_requirements: formData.language_requirements || null,
        contract_period_id: parseInt(selectedContractPeriod),
        required_skill_ids: selectedSkillIds,
        special_skills: '',
        other_specifications: formData.other_specifications || null
      };

      if (selectedWoman) {
        requestData.requested_woman_id = selectedWoman.id;
        requestData.requested_woman_name = selectedWoman.name;
      }

      if (onSubmit) {
        await onSubmit(requestData);
        setSuccess(true);
        showToast(
          'success',
          t('alertSuccess') || 'Success',
          t('requestSuccess') || 'Your request has been submitted successfully!'
        );
        setTimeout(() => onClose(), 3000);
      } else {
        const token = localStorage.getItem('token');
        if (!token) {
          showToast(
            'warning',
            t('alertWarning') || 'Warning',
            t('loginToRequest') || 'Please login to request women'
          );
          setLoading(false);
          return;
        }

        await axios.post(
          'https://hsn.urugowoc.org.rw/backend/api/employer/request',
          requestData,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setSuccess(true);
        showToast(
          'success',
          t('alertSuccess') || 'Success',
          t('requestSuccess') || 'Your request has been submitted successfully!'
        );
        setTimeout(() => onClose(), 3000);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit request';
      showToast('error', t('alertError') || 'Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleContractPeriodChange = (e) => {
    const value = e.target.value;
    setSelectedContractPeriod(value);
    setFormData(prev => ({ ...prev, contract_period_id: value }));
  };

  // ========== RENDER SELECTED WOMAN INFO ==========
  const renderSelectedWomanInfo = () => {
    if (!selectedWoman) return null;

    return (
      <div className="request-selected-woman-info">
        <div className="request-woman-card">
          {/* <div className="request-woman-image request-woman-image-icon">
  <div className="request-woman-icon-wrapper">
    <FaUser className="request-woman-person-icon" aria-hidden="true" />
  </div>
  <div className={`request-woman-status ${selectedWoman.is_available ? 'available' : 'busy'}`}>
    {selectedWoman.is_available ? (t('available') || 'Available') : (t('working') || 'Not Available')}
  </div>
</div> */}
          <div className="request-woman-details">
            <h3>{selectedWoman.name}</h3>
            <p className="request-woman-department">
              <FaGraduationCap /> {selectedWoman.department_name}
            </p>
            <div className="request-woman-skills">
              <span>{selectedWoman.skills}</span>
            </div>
            <div className="request-woman-meta">
              <span className="request-woman-experience">
                <FaClock /> {selectedWoman.experience_years || 0} {t('years') || 'years'}
              </span>
              {selectedWoman.certificate_url && (
                <span className={`request-woman-certificate ${selectedWoman.certificate_verified ? 'verified' : 'unverified'}`}>
                  <FaCertificate />
                  {selectedWoman.certificate_verified ?
                    <FaCheckCircle className="verified-icon" /> :
                    <FaTimesCircle className="unverified-icon" />
                  }
                  {selectedWoman.certificate_verified ?
                    (t('certificateVerified') || 'Verified') :
                    (t('certificateUnverified') || 'Pending')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="request-woman-note">
          <p>{t('specificWomanRequest') || 'You are requesting'} <strong>{selectedWoman.name}</strong> {t('specifically') || 'specifically'}.</p>
          <p>{t('singleWomanNote') || 'Number of employees is automatically set to 1 for individual woman requests.'}</p>
        </div>
      </div>
    );
  };

  // ========== STEP 1: ACCOUNT TYPE ==========
  const renderStep1 = () => (
    <div className="request-step-content">
      <h2>{t('step1Title') || 'Tell us about you'}</h2>
      <p>{t('step1Description') || 'Find trained women in our diverse community'}</p>

      {selectedWoman && renderSelectedWomanInfo()}

      <div className="request-account-type-selector">
        <div
          className={`request-account-type-option ${formData.user_type === 'individual' ? 'selected' : ''}`}
          onClick={() => setFormData(prev => ({ ...prev, user_type: 'individual' }))}
        >
          <FaUser className="request-account-icon" />
          <h4>{t('individual') || 'Individual'}</h4>
          <p>{t('individualDesc') || "I'm hiring for myself"}</p>
        </div>

        <div
          className={`request-account-type-option ${formData.user_type === 'company' ? 'selected' : ''}`}
          onClick={() => setFormData(prev => ({ ...prev, user_type: 'company' }))}
        >
          <FaBuilding className="request-account-icon" />
          <h4>{t('company') || 'Company'}</h4>
          <p>{t('companyDesc') || "I'm hiring for my company"}</p>
        </div>
      </div>

      {formData.user_type === 'company' && (
        <div className="request-form-group">
          <label>{t('companyNameLabel') || 'Company name'} *</label>
          <input
            type="text"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            placeholder={t('companyNamePlaceholder') || 'Enter company name'}
            required
          />
        </div>
      )}

      <div className="request-modal-actions">
        <button
          type="button"
          className="request-btn request-btn-primary"
          onClick={() => setStep(2)}
        >
          {t('continue') || 'Continue'} <FaArrowRight />
        </button>
      </div>
    </div>
  );

  // ========== LOGIN FORM (EMAIL ONLY) ==========
  const renderLoginForm = () => (
    <div className="request-step-content">
      <h2>{t('loginTitle') || 'Login to Your Account'}</h2>
      <p>{t('loginDescriptionEmailOnly') || 'Enter your email to continue — no password needed'}</p>

      {selectedWoman && (
        <div className="request-login-woman-info">
          <p>{t('requestingWoman') || 'You are requesting:'} <strong>{selectedWoman.name}</strong></p>
        </div>
      )}

      <form onSubmit={handleLogin} className="request-form">
        {loginError && (
          <div className="request-error-message">{loginError}</div>
        )}

        <div className="request-form-group full-width">
          <label>{t('emailLabel') || 'Email Address'} *</label>
          <input
            type="email"
            name="email"
            value={loginData.email}
            onChange={handleLoginChange}
            placeholder={t('emailPlaceholder') || 'Enter your email'}
            required
            disabled={loading}
            autoFocus
          />
          <small>{t('emailLoginHint') || "We'll look up your account by email. No password needed."}</small>
        </div>

        <div className="request-modal-actions">
          <button
            type="button"
            className="request-btn request-btn-outline"
            onClick={() => setShowLoginForm(false)}
            disabled={loading}
          >
            <FaArrowLeft /> {t('backToRegister') || 'Back to Register'}
          </button>
          <button
            type="submit"
            className="request-btn request-btn-primary"
            disabled={loading}
          >
            {loading ? (t('loggingIn') || 'Logging in...') : (t('signIn') || 'Login')}
          </button>
        </div>
      </form>
    </div>
  );

  // ========== REGISTER FORM (EMAIL ONLY, NO PASSWORD) ==========
  const renderRegisterForm = () => (
    <div className="request-step-content">
      <h2>{t('step2Title') || 'Create Your Account'}</h2>
      <p>{t('step2DescriptionNoPassword') || 'Just enter your email — no password required'}</p>

      {selectedWoman && (
        <div className="request-register-woman-info">
          <p>{t('requestingWoman') || 'You are requesting:'} <strong>{selectedWoman.name}</strong></p>
        </div>
      )}

      <div className="request-login-toggle">
        <p>
          {t('alreadyHaveAccount') || 'Already have an account?'}{' '}
          <button
            type="button"
            className="request-login-link"
            onClick={() => setShowLoginForm(true)}
          >
            <FaSignInAlt /> {t('loginHere') || 'Login here'}
          </button>
        </p>
      </div>

      <form onSubmit={handleRegister} className="request-form">
        {registrationError && (
          <div className="request-error-message">{registrationError}</div>
        )}

        <div className="request-form-row">
          <div className="request-form-group">
            <label>{t('fullNameLabel') || 'Full Name'} *</label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder={t('fullNamePlaceholder') || 'Enter your full name'}
              required
              disabled={loading}
            />
          </div>

          <div className="request-form-group">
            <label>{t('emailLabel') || 'Email'} *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('emailPlaceholder') || 'Enter your email'}
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="request-form-row">
          <div className="request-form-group">
            <label>{t('phoneLabel') || 'Phone Number'}</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('phonePlaceholder') || 'Enter your phone number'}
              disabled={loading}
            />
          </div>
          <div className="request-form-group"></div>
        </div>

        <div className="request-form-group full-width">
          <label className="request-checkbox-label">
            <input type="checkbox" required disabled={loading} />
            {t('agreeToTerms') || 'I agree to the Terms of Service and Privacy Policy'}
          </label>
        </div>

        <div className="request-modal-actions">
          <button
            type="button"
            className="request-btn request-btn-outline"
            onClick={() => setStep(1)}
            disabled={loading}
          >
            <FaArrowLeft /> {t('back') || 'Back'}
          </button>
          <button
            type="submit"
            className="request-btn request-btn-primary"
            disabled={loading}
          >
            {loading ? (t('creatingAccount') || 'Creating Account...') : (t('createAndContinue') || 'Create Account & Continue')}
          </button>
        </div>
      </form>
    </div>
  );

  const renderStep2 = () => {
    return showLoginForm ? renderLoginForm() : renderRegisterForm();
  };

  // ========== STEP 3: REQUEST FORM ==========
  const renderStep3 = () => (
    <form onSubmit={handleSubmitRequest} className="request-form">
      <h2>{t('step3Title') || 'Request Trained Women'}</h2>
      <p>{t('step3Description') || 'Fill out the form below to request skilled women from our center'}</p>

      {selectedWoman && renderSelectedWomanInfo()}

      {!selectedWoman && (
        <div className="request-form-row">
          <div className="request-form-group">
            <label>{t('selectDepartment') || 'Select Department'} *</label>
            <select
              name="department_id"
              value={formData.department_id}
              onChange={handleChange}
              required
              disabled={!!departmentId || loading}
            >
              <option value="">{t('chooseDepartment') || 'Choose a department'}</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          <div className="request-form-group">
            <label>{t('employeesNeeded') || 'Number of Employees'} *</label>
            <input
              type="number"
              name="number_of_employees"
              value={formData.number_of_employees}
              onChange={handleChange}
              min="1"
              max="50"
              required
              disabled={loading}
            />
          </div>
        </div>
      )}

      <div className="request-form-row">
        <div className="request-form-group">
          <label>{t('workType') || 'Work Type'} *</label>
          <select
            name="work_type"
            value={formData.work_type}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="full_time">{t('fullTime') || 'Full Time'}</option>
            <option value="part_time">{t('partTime') || 'Part Time'}</option>
            <option value="contract">{t('contract') || 'Contract'}</option>
            <option value="seasonal">{t('seasonal') || 'Seasonal'}</option>
          </select>
        </div>

        <div className="request-form-group">
          <label>{t('contractPeriod') || 'Contract Period'} *</label>
          <select
            name="contract_period_id"
            value={selectedContractPeriod}
            onChange={handleContractPeriodChange}
            required
            disabled={loading || loadingContractPeriods || contractPeriods.length === 0}
          >
            <option value="">{t('selectContractPeriod') || 'Select contract period'}</option>
            {contractPeriods.map(period => (
              <option key={period.id} value={period.id.toString()}>
                {period.period_name} ({period.months} months)
              </option>
            ))}
          </select>
          {loadingContractPeriods && (
            <small style={{ color: '#2196F3' }}>
              {t('loadingContractPeriods') || 'Loading contract periods...'}
            </small>
          )}
        </div>
      </div>

     <div className="request-form-section-header">
  <h3>{t('personalInformation') || 'Personal Information'}</h3>
</div>

<div className="request-form-row">
  <div className="request-form-group">
    <label>{t('yourFullName') || 'Your Full Name'} *</label>
    <input
      type="text"
      name="employer_name"
      value={formData.employer_name}
      onChange={handleChange}
      required
      disabled={loading}
      readOnly={isLoggedIn}       /* ✅ read-only for logged-in users */
      className={isLoggedIn ? 'request-input-readonly' : ''}
    />
  </div>

  <div className="request-form-group">
    <label>{t('email') || 'Email'} *</label>
    <input
      type="email"
      name="employer_email"
      value={formData.employer_email}
      onChange={handleChange}
      required
      disabled={loading}
      readOnly={isLoggedIn}       /* ✅ read-only for logged-in users */
      className={isLoggedIn ? 'request-input-readonly' : ''}
    />
  </div>
</div>

<div className="request-form-row">
  <div className="request-form-group">
    <label>{t('phoneNumber') || 'Phone Number'} *</label>
    <input
      type="tel"
      name="employer_phone"
      value={formData.employer_phone}
      onChange={handleChange}
      required
      disabled={loading}
    />
  </div>
  <div className="request-form-group"></div>
</div>
      <div className="request-specifications-section">
        <div className="request-form-section-header">
          <h3>{t('womanSpecifications') || 'Woman Specifications (Optional)'}</h3>
          <p>{t('specificationsDescription') || 'Provide additional requirements for the women you want to hire'}</p>
        </div>

        <div className="request-form-row">
          <div className="request-form-group">
            <label>{t('ageRange') || 'Preferred Age Range'}</label>
            <select name="age_range" value={formData.age_range} onChange={handleChange} disabled={loading}>
              <option value="">{t('noPreference') || 'No preference'}</option>
              <option value="18-25">18-25 {t('years') || 'years'}</option>
              <option value="26-35">26-35 {t('years') || 'years'}</option>
              <option value="36-45">36-45 {t('years') || 'years'}</option>
              <option value="46+">46+ {t('years') || 'years'}</option>
            </select>
          </div>

          <div className="request-form-group">
            <label>{t('experienceLevel') || 'Experience Level'}</label>
            <select name="experience_level" value={formData.experience_level} onChange={handleChange} disabled={loading}>
              <option value="">{t('noPreference') || 'No preference'}</option>
              <option value="beginner">{t('beginner') || 'Beginner'} (0-2 {t('years') || 'years'})</option>
              <option value="intermediate">{t('intermediate') || 'Intermediate'} (2-5 {t('years') || 'years'})</option>
              <option value="advanced">{t('advanced') || 'Advanced'} (5-10 {t('years') || 'years'})</option>
              <option value="expert">{t('expert') || 'Expert'} (10+ {t('years') || 'years'})</option>
            </select>
          </div>
        </div>

        <div className="request-form-row">
          <div className="request-form-group">
            <label>{t('skillLevel') || 'Skill Level Required'}</label>
            <select name="skill_level" value={formData.skill_level} onChange={handleChange} disabled={loading}>
              <option value="">{t('noPreference') || 'No preference'}</option>
              <option value="basic">{t('basic') || 'Basic Skills'}</option>
              <option value="intermediate">{t('intermediate') || 'Intermediate Skills'}</option>
              <option value="advanced">{t('advanced') || 'Advanced Skills'}</option>
            </select>
          </div>

          <div className="request-form-group">
            <label>{t('educationLevel') || 'Education Level'}</label>
            <select name="education_level" value={formData.education_level} onChange={handleChange} disabled={loading}>
              <option value="">{t('noPreference') || 'No preference'}</option>
              <option value="primary">{t('primary') || 'Primary Education'}</option>
              <option value="secondary">{t('secondary') || 'Secondary Education'}</option>
              <option value="vocational">{t('vocational') || 'Vocational Training'}</option>
              <option value="university">{t('university') || 'University Degree'}</option>
            </select>
          </div>
        </div>

        <div className="request-form-row">
          <div className="request-form-group">
            <label>{t('languageRequirements') || 'Language Requirements'}</label>
            <select name="language_requirements" value={formData.language_requirements} onChange={handleChange} disabled={loading}>
              <option value="">{t('noPreference') || 'No preference'}</option>
              <option value="kinyarwanda">{t('kinyarwanda') || 'Kinyarwanda'}</option>
              <option value="english">{t('english') || 'English'}</option>
              <option value="french">{t('french') || 'French'}</option>
              <option value="kinyarwanda_english">{t('kinyarwandaEnglish') || 'Kinyarwanda & English'}</option>
              <option value="all">{t('anyLanguage') || 'Any language'}</option>
            </select>
          </div>

          <div className="request-form-group full-width">
            <label>{t('requiredSkills') || 'Required Competence Skills'}</label>
            <div className="request-skills-selection">
              {competenceSkills.length > 0 ? (
                <div className="request-skills-checkbox-group">
                  {competenceSkills.map(skill => (
                    <label key={skill.id} className="request-skill-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedSkillIds.includes(skill.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSkillIds([...selectedSkillIds, skill.id]);
                          } else {
                            setSelectedSkillIds(selectedSkillIds.filter(id => id !== skill.id));
                          }
                        }}
                        disabled={loading}
                      />
                      <span>{skill.skill_name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p>{t('noSkillsForDepartment') || 'No skills available for this department'}</p>
              )}
            </div>
          </div>
        </div>

        <div className="request-form-group full-width">
          <label>{t('otherSpecifications') || 'Other Specifications'}</label>
          <textarea
            name="other_specifications"
            value={formData.other_specifications}
            onChange={handleChange}
            placeholder={t('otherSpecificationsPlaceholder') || 'Any other specific requirements or preferences...'}
            rows="3"
            disabled={loading}
          />
        </div>
      </div>

      <div className="request-modal-actions">
        {!isLoggedIn && (
          <button
            type="button"
            className="request-btn request-btn-outline"
            onClick={() => setStep(2)}
            disabled={loading}
          >
            <FaArrowLeft /> {t('back') || 'Back'}
          </button>
        )}
        <button type="submit" className="request-btn request-btn-primary" disabled={loading}>
          {loading ? (t('submitting') || 'Submitting...') : (t('submitRequest') || 'Submit Request')}
        </button>
      </div>
    </form>
  );

  const renderSuccess = () => (
    <div className="request-success-message">
      <FaCheck className="request-success-icon" />
      <h3>{t('successTitle') || 'Request Submitted Successfully!'}</h3>
      <p>{t('successMessage') || 'Your request has been received. We will contact you shortly.'}</p>

      {selectedWoman && (
        <p className="request-success-woman">
          {t('youRequested') || 'You requested:'} <strong>{selectedWoman.name}</strong>
        </p>
      )}

      {(formData.age_range || formData.experience_level || formData.skill_level) && (
        <div className="request-success-specifications">
          <h4>{t('yourSpecifications') || 'Your Specifications:'}</h4>
          <ul>
            {formData.age_range && <li>{t('ageRange') || 'Age Range'}: {formData.age_range}</li>}
            {formData.experience_level && <li>{t('experienceLevel') || 'Experience'}: {formData.experience_level}</li>}
            {formData.skill_level && <li>{t('skillLevel') || 'Skill Level'}: {formData.skill_level}</li>}
            {formData.education_level && <li>{t('educationLevel') || 'Education'}: {formData.education_level}</li>}
            {formData.language_requirements && <li>{t('languages') || 'Languages'}: {formData.language_requirements}</li>}
          </ul>
        </div>
      )}
    </div>
  );

  // ========== TOAST ICON HELPER ==========
  const getToastIcon = (type) => {
    switch (type) {
      case 'success':
        return <FaCheckCircle />;
      case 'error':
        return <FaTimesCircle />;
      case 'warning':
        return <FaExclamationTriangle />;
      default:
        return <FaInfoCircle />;
    }
  };

  return (
    <>
      {/* ========== TOAST CONTAINER ========== */}
      {toasts.length > 0 && (
        <div className="request-toast-container">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`request-toast request-toast-${toast.type}`}
              role="alert"
            >
              <div className="request-toast-icon">
                {getToastIcon(toast.type)}
              </div>
              <div className="request-toast-content">
                {toast.title && (
                  <div className="request-toast-title">{toast.title}</div>
                )}
                {toast.message && (
                  <div className="request-toast-message">{toast.message}</div>
                )}
              </div>
              <button
                type="button"
                className="request-toast-close"
                onClick={() => removeToast(toast.id)}
                aria-label={t('close') || 'Close'}
              >
                <FaTimes />
              </button>
              <div
                className="request-toast-progress"
                style={{ animationDuration: `${toast.duration}ms` }}
              />
            </div>
          ))}
        </div>
      )}

      {/* ========== MAIN MODAL ========== */}
      <div className="request-modal-overlay">
        <div className="request-modal-content">
          <button
            className="request-close-modal"
            onClick={onClose}
            aria-label={t('close') || 'Close'}
            disabled={loading}
          >
            <FaTimes />
          </button>

          <div className="request-modal-header">
            <div className="request-step-indicator">
              <div className={`request-step ${step >= 1 ? 'active' : ''}`}>
                <span className="request-step-number">1</span>
                <span className="request-step-label">{t('accountType') || 'Account Type'}</span>
              </div>
              <div className={`request-step ${step >= 2 ? 'active' : ''}`}>
                <span className="request-step-number">2</span>
                <span className="request-step-label">{t('account') || 'Account'}</span>
              </div>
              <div className={`request-step ${step >= 3 ? 'active' : ''}`}>
                <span className="request-step-number">3</span>
                <span className="request-step-label">{t('request') || 'Request'}</span>
              </div>
            </div>
          </div>

          <div className="request-modal-body">
            {success ? renderSuccess() : (
              <>
                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
                {step === 3 && renderStep3()}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RequestModal;